/* import __COLOCATED_TEMPLATE__ from './workspace-phone-number.hbs'; */
/* RESPONSIBLE TEAM: team-ai-agent */
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { inject as service } from '@ember/service';
import type Store from '@ember-data/store';
import { action } from '@ember/object';
import type IntlService from 'ember-intl/services/intl';
import type FinVoiceNumberConfiguration from 'embercom/models/fin-voice/number-configuration';

interface Signature {
  Element: HTMLDivElement;
  Args: {
    accordion: any;
    ruleset: $TSFixMe;
    numberConfiguration: FinVoiceNumberConfiguration;
    updateWorkspacePhoneNumber: (workspacePhoneNumber: string) => Promise<void>;
  };
}

interface ListOption {
  listOptionId: string;
  label: string;
  archived: boolean;
}

export default class WorkspacePhoneNumber extends Component<Signature> {
  @service declare store: Store;
  @service declare intl: IntlService;
  @tracked phoneNumbers: Array<{ value: string; text: string }> = [];
  @tracked workspacePhoneNumber: string | null = null;

  constructor(owner: unknown, args: Signature['Args']) {
    super(owner, args);
    this.loadPhoneNumbers();
  }

  get savingDisabled() {
    return (
      this.hasNoPhoneNumbers ||
      !this.workspacePhoneNumber ||
      this.args.ruleset.isSaving ||
      this.args.numberConfiguration?.isSaving
    );
  }

  get errorMessage() {
    if (this.hasNoPhoneNumbers) {
      return this.intl.t(
        'operator.fin.setup.tabs.setup-and-go-live.phone.workspace-phone-number.error-no-phone-numbers',
      );
    }

    if (!this.workspacePhoneNumber) {
      return this.intl.t(
        'operator.fin.setup.tabs.setup-and-go-live.phone.workspace-phone-number.error-no-phone-number-selected',
      );
    }

    return undefined;
  }

  get savedValue() {
    return this.args.numberConfiguration?.workspacePhoneNumber;
  }

  get workspacePhoneNumberLabel() {
    let label = this.phoneNumbers.find(
      (phoneNumber) => phoneNumber.value === this.savedValue,
    )?.text;

    if (!label || this.savedValue === label) {
      return this.savedValue;
    }

    return this.intl.t(
      'operator.fin.setup.tabs.setup-and-go-live.phone.workspace-phone-number.phone-number-label',
      {
        label,
        phoneNumber: this.savedValue,
      },
    );
  }

  get hasNoPhoneNumbers() {
    return !this.phoneNumbers?.length;
  }

  async loadPhoneNumbers() {
    let phoneDescriptor = this.store.peekRecord(
      'conversation-attributes/descriptor',
      'workspace_phone_number',
    );

    if (phoneDescriptor) {
      this.phoneNumbers = phoneDescriptor.listOptions
        .filter((option: ListOption) => !option.archived)
        .map((option: ListOption) => ({
          value: option.listOptionId,
          text: option.label,
        }));
    }

    this.workspacePhoneNumber = this.savedValue;
  }

  @action
  resetPredicate() {
    this.workspacePhoneNumber = this.savedValue;
  }

  @action
  updatePhoneNumber(value: string) {
    this.workspacePhoneNumber = value;
  }

  @action
  async updateWorkspacePhoneNumber() {
    if (!this.workspacePhoneNumber) {
      return;
    }

    await this.args.updateWorkspacePhoneNumber(this.workspacePhoneNumber);
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Operator::Fin::Setup::Sections::WhoWillTalkToFin::WorkspacePhoneNumber': typeof WorkspacePhoneNumber;
  }
}
